'use client';

import Link from 'next/link';

import { useTranslations } from '@/components/language-provider';

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" {...props}>
      <path d="M4 10.5 8.5 15 16 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

type CourtIntelligenceGetStartedScreenProps = {
  primaryHref?: string;
  secondaryHref?: string;
};

export function CourtIntelligenceGetStartedScreen({
  primaryHref,
  secondaryHref,
}: CourtIntelligenceGetStartedScreenProps = {}) {
  const { messages } = useTranslations();
  const content = messages.courtIntelligence.onboardingEntry;

  return (
    <div className="space-y-8">
      <section className="rounded-3xl border border-border/60 bg-surface/90 p-8 shadow-xl shadow-primary/10">
        <div className="mx-auto flex max-w-3xl flex-col gap-5 text-center">
          <span className="mx-auto inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {content.eyebrow}
          </span>
          <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            {content.title}
          </h1>
          <p className="text-base text-muted-foreground sm:text-lg">{content.description}</p>
          <p className="text-sm text-muted-foreground">{content.note}</p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Link
              href={primaryHref ?? content.primaryHref}
              className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow transition hover:-translate-y-0.5 hover:bg-primary/90"
            >
              {content.primaryCta}
            </Link>
            <Link
              href={secondaryHref ?? content.secondaryHref}
              className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow"
            >
              {content.secondaryCta}
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <article className="space-y-6 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10">
          <div className="space-y-3">
            <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">{content.stepsTitle}</h2>
            <p className="text-sm text-muted-foreground">{content.description}</p>
          </div>
          <div className="space-y-4">
            {content.steps.map((step, index) => (
              <div key={step.title} className="flex items-start gap-3 rounded-2xl border border-border/50 bg-background px-4 py-4">
                <span className="inline-flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                  {index + 1}
                </span>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">{content.checklistTitle}</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {content.checklist.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-2xl border border-border/50 bg-background px-4 py-3">
                  <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </article>

        <aside className="flex h-full flex-col gap-4 rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/15 to-background p-8 shadow-lg shadow-primary/10">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {content.foundingCallout.eyebrow}
          </span>
          <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">{content.foundingCallout.title}</h2>
          <p className="text-sm text-muted-foreground sm:text-base">{content.foundingCallout.description}</p>
          <Link
            href={content.foundingCallout.href}
            className="inline-flex items-center justify-center self-start rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            {content.foundingCallout.cta}
          </Link>
          <div className="mt-auto flex flex-col items-end gap-3">
            <Link
              href={content.backHref}
              className="inline-flex items-center text-sm font-semibold text-primary underline-offset-4 transition hover:underline"
            >
              {content.backCta}
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
